"use client";

import { useState } from "react";
import ContactMap from "../../components/ContactMap";

const FIELD_DEFS = [
  { key: "name", label: "Your name", placeholder: "First and last name", type: "text" },
  { key: "email", label: "Email", placeholder: "you@company.com", type: "email" },
  { key: "company", label: "Company", placeholder: "Company or organization", type: "text" }
];
const BUDGETS = ["Under $25k", "$25k – $75k", "$75k – $150k", "$150k+", "Not sure yet"];

const FAQS = [
  { q: "What's your typical project timeline?", a: "Most brand identity projects run 6–10 weeks; websites run 8–14 depending on scope. We'll give you a real schedule once we understand what you need, not a generic range." },
  { q: "Do you have a minimum budget?", a: "We generally take on projects starting around $25k. If you're not sure where yours lands, tell us what you need — we'll be straight with you either way." },
  { q: "Do you work with startups and small businesses?", a: "Yes. Alongside our enterprise and institutional clients, we regularly work with founders building a brand from scratch — the process just gets scoped differently." },
  { q: "What's actually included in a brand identity project?", a: "Strategy, naming (if needed), visual identity — logo, colour, type, imagery — and a system to apply it consistently, delivered as brand guidelines your team and partners can actually use." },
  { q: "Do you work with clients outside Toronto?", a: "Regularly. Our studio is downtown Toronto, but most of our work happens over video calls and shared docs — location has never really been the constraint." },
  { q: "What happens after launch — do you offer ongoing support?", a: "Some clients wrap up after delivery; others keep us on retainer for ongoing design, web updates, or campaign work. We'll figure out what makes sense for you as we go." }
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="px-faq-row" onClick={onToggle} role="button" tabIndex={0} aria-expanded={open} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle())}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
        <span style={{ fontSize: 20, fontWeight: 500, letterSpacing: "-0.012em" }}>{item.q}</span>
        <span className="px-faq-toggle" style={{ transform: open ? "rotate(45deg)" : "none" }}>+</span>
      </div>
      <div className="px-faq-answer" style={{ maxHeight: open ? 200 : 0 }}>
        <p style={{ margin: "16px 0 4px", fontSize: 16, lineHeight: 1.6, color: "var(--px-text-secondary)", maxWidth: 640 }}>{item.a}</p>
      </div>
    </div>
  );
}

function inputStyle(hasError) {
  return {
    width: "100%",
    padding: "12px 0",
    fontSize: 17,
    fontFamily: "inherit",
    color: "var(--px-text)",
    background: "none",
    border: "none",
    borderBottom: "1px solid " + (hasError ? "var(--px-error)" : "var(--px-border)"),
    outline: "none"
  };
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [budget, setBudget] = useState("");
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sentName, setSentName] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "We'd like to know who we're talking to.";
    if (!form.email.trim() || !form.email.includes("@")) next.email = "That email doesn't look right.";
    if (!form.message.trim()) next.message = "A sentence or two is plenty — just not blank.";
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) return;

    setSending(true);
    setSendError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, budget })
      });
      if (!res.ok) throw new Error("send failed");
      if (typeof window !== "undefined" && window.plausible) {
        window.plausible("Contact Form Submitted");
      }
      setSentName(form.name.trim().split(" ")[0]);
      setSent(true);
    } catch {
      setSendError("Something went wrong sending that — mind trying again, or emailing us directly?");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setForm({ name: "", email: "", company: "", message: "" });
    setBudget("");
    setErrors({});
    setSendError("");
  };

  const filled = [form.name, form.email, form.message].filter((v) => v && v.trim()).length;

  return (
    <main id="main-content">
      <section className="px-sec px-contact-grid" style={{ paddingTop: 88, paddingBottom: 96, display: "grid", gridTemplateColumns: "1fr 0.68fr", gap: 80 }}>
        <div>
          <h1 className="px-h1" style={{ fontSize: "clamp(40px, 5.6vw, 76px)", lineHeight: 1 }}>Start a Project</h1>
          <p style={{ margin: "28px 0 48px", maxWidth: 560, fontSize: 19, lineHeight: 1.55, color: "var(--px-text-secondary)" }}>
            Tell us what you're working on. We'll come back within two business days.
          </p>

          {sent ? (
            <div>
              <h2 style={{ margin: "0 0 12px", fontSize: 34, fontWeight: 200, letterSpacing: "-0.02em", fontFamily: "'ivyjournal', Georgia, serif" }}>Thanks, {sentName} — message received.</h2>
              <p style={{ margin: "0 0 28px", fontSize: 16, color: "var(--px-text-secondary)" }}>We'll come back to you within two business days.</p>
              <button type="button" onClick={resetForm} style={{ background: "none", border: "none", borderBottom: "2px solid var(--px-text)", padding: "0 0 2px", fontSize: 16, fontWeight: 500, fontFamily: "inherit", color: "var(--px-text)", cursor: "pointer" }}>
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "grid", gap: 30, maxWidth: 620 }}>
              {FIELD_DEFS.map((f) => {
                const fieldId = "field-" + f.key;
                const errorId = fieldId + "-error";
                const required = f.key !== "company";
                const error = errors[f.key];
                return (
                  <label key={f.key} style={{ display: "block" }}>
                    <span className="px-mono-label" style={{ display: "block", marginBottom: 10 }}>{f.label}</span>
                    <input
                      id={fieldId}
                      value={form[f.key]}
                      onChange={setField(f.key)}
                      placeholder={f.placeholder}
                      type={f.type}
                      aria-required={required}
                      aria-invalid={!!error}
                      aria-describedby={errorId}
                      style={inputStyle(!!error)}
                    />
                    {error && <span id={errorId} role="alert" style={{ display: "block", marginTop: 8, fontSize: 14, color: "var(--px-error)" }}>{error}</span>}
                  </label>
                );
              })}

              <div>
                <span className="px-mono-label" style={{ display: "block", marginBottom: 12 }}>Budget range</span>
                <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
                  {BUDGETS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setBudget(b)}
                      aria-pressed={budget === b}
                      style={{
                        padding: "0 0 4px",
                        fontSize: 12,
                        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        cursor: "pointer",
                        border: "none",
                        borderBottom: "1px solid " + (budget === b ? "var(--px-text)" : "transparent"),
                        background: "none",
                        color: budget === b ? "var(--px-text)" : "var(--px-text-tertiary)"
                      }}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              <label style={{ display: "block" }}>
                <span className="px-mono-label" style={{ display: "block", marginBottom: 10 }}>What do you need?</span>
                <textarea
                  id="field-message"
                  value={form.message}
                  onChange={setField("message")}
                  rows={5}
                  placeholder="A rebrand, a website, a report — or you're not sure yet."
                  aria-required="true"
                  aria-invalid={!!errors.message}
                  aria-describedby="field-message-error"
                  style={{ ...inputStyle(!!errors.message), resize: "vertical", lineHeight: 1.55 }}
                ></textarea>
                {errors.message && <span id="field-message-error" role="alert" style={{ display: "block", marginTop: 8, fontSize: 14, color: "var(--px-error)" }}>{errors.message}</span>}
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                <button
                  type="submit"
                  disabled={sending}
                  style={{ background: "none", color: "var(--px-text)", border: "none", borderBottom: "2px solid var(--px-text)", padding: "0 0 3px", fontSize: 17, fontWeight: 500, fontFamily: "inherit", cursor: sending ? "default" : "pointer", opacity: sending ? 0.5 : 1 }}
                >
                  {sending ? "Sending…" : "Send it"}
                </button>
                <span className="px-mono-label">{filled} of 3 required fields</span>
              </div>
              {sendError && <p role="alert" style={{ margin: 0, fontSize: 14, color: "var(--px-error)" }}>{sendError}</p>}
            </form>
          )}
        </div>

        <aside className="px-contact-aside" style={{ borderLeft: "1px solid var(--px-border)", paddingLeft: 44 }}>
          <div style={{ marginBottom: 40 }}>
            <div className="px-mono-label" style={{ marginBottom: 14 }}>Find us</div>
            <div style={{ fontSize: 17, lineHeight: 1.6 }}>33 Bloor St E, Floor 5<br />Toronto, ON M4W 3H1</div>
          </div>
          <div style={{ marginBottom: 40 }}>
            <div className="px-mono-label" style={{ marginBottom: 14 }}>Say hello</div>
            <a href="mailto:info@pixelsandpencils.ca" style={{ display: "block", fontSize: 17, marginBottom: 8 }}>info@pixelsandpencils.ca</a>
            <a href="tel:+16478804266" style={{ display: "block", fontSize: 17 }}>+647 880 4266</a>
          </div>
          <div>
            <div className="px-mono-label" style={{ marginBottom: 14 }}>Follow</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <a href="https://www.linkedin.com/company/pixels-and-pencils/" target="_blank" rel="noopener" style={{ fontSize: 17 }}>LinkedIn ↗</a>
              <a href="https://www.instagram.com/pixels.and/" target="_blank" rel="noopener" style={{ fontSize: 17 }}>Instagram ↗</a>
            </div>
          </div>
        </aside>
      </section>

      <section className="px-sec" style={{ paddingTop: 0, paddingBottom: 96 }}>
        <div
          role="img"
          aria-label="Map showing the Pixels& studio at 33 Bloor St E, Floor 5, Toronto"
          style={{ borderRadius: 18, overflow: "hidden", border: "1px solid var(--px-border)", height: "clamp(280px, 32vw, 480px)" }}
        >
          <ContactMap zoom={16} />
        </div>
      </section>

      <section className="px-sec" style={{ paddingTop: 0, paddingBottom: 96 }}>
        <h2 style={{ margin: "0 0 40px", fontSize: "clamp(28px, 3vw, 40px)", letterSpacing: "-0.025em", fontWeight: 400 }}>FAQs</h2>
        <div style={{ borderBottom: "1px solid var(--px-border)" }}>
          {FAQS.map((item, i) => (
            <FaqItem key={item.q} item={item} open={openFaq === i} onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))} />
          ))}
        </div>
      </section>
    </main>
  );
}
